import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { AppUrls } from '../../../app/AppUrls';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { IContentDefinition } from './IContentDefinition';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { DateTimeCell } from '../../components/listView/dataGridCells/DateTimeCells';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { Lense } from '../SearchResultTypes';
import { ILocationInfo, LocationTypes } from '../ILocationInfo';
import { SessionManagementService } from '../../services/SessionManagementService';
import { GetLensesResult } from '../httpClient/MetaDataResults';
import { IUser } from '../IUser';
import { UserMapper } from '../../../common/utils/UserMapper';
import { SortBy } from '../SortSelection';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';

export const lenseContentDefinition: IContentDefinition<Lense, GetLensesResult> = {
  getSectionTitle: () => Messages.LenseSectionTitle(),
  getUrl: (urls) => urls.searchLensesUrl(),
  getPath: (urls) => urls.searchLensesPath(),
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        crassData: crassDataItem,
        contentType: ContentType.lens,
        name: crassDataItem.title,
        id: crassDataItem.id.toString(),
        description: crassDataItem.description || '',
        url: new AppUrls({
          siteUrlName: SessionManagementService.instance.sessionSiteUrlName(),
          queryString: window.location.search,
        }).lensUrl(crassDataItem.id.toString()),
        modifiedTime: crassDataItem.modifiedTime || '',
      };
    });
  },
  mapMetadata: (searchResultItems, metadata) => {
    const metaDataUserInfo: { [ownerId: string]: IUser } = {};
    const metaDataLenses: { [workbookId: string]: { location: ILocationInfo; createdAt: string } } = {};
    const favoriteMetaData: { [id: string]: boolean } = {};
    if (metadata.total > 0) {
      metadata.items.users.forEach((item) => {
        metaDataUserInfo[item.id] = item;
      });
      metadata.items.projects.forEach((item) => {
        item.contents.LENS.forEach((data: number) => {
          metaDataLenses[data.toString()] = {
            location: { name: item.name, id: item.id.toString(), type: LocationTypes.Project },
            createdAt: '',
          };
        });
      });
      metadata.items.lenses.forEach((item: { id: string; createdAt: string }) => {
        if (metaDataLenses[item.id]) {
          metaDataLenses[item.id].createdAt = item.createdAt;
        }
      });
      metadata.items.favorites.forEach((id) => {
        favoriteMetaData[id] = true;
      });
    }
    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      const user = crassDataItem.ownerId ? metaDataUserInfo[crassDataItem.ownerId] : undefined;
      const owner = UserMapper.mapOne(user);

      return {
        ...searchResultItem,
        owner: owner,
        location: metaDataLenses[crassDataItem.id.toString()]
          ? metaDataLenses[crassDataItem.id.toString()].location
          : ({} as ILocationInfo),
        createdAt: metaDataLenses[crassDataItem.id.toString()] ? metaDataLenses[crassDataItem.id.toString()].createdAt : '',
        favorite: favoriteMetaData[crassDataItem.id.toString()] ? favoriteMetaData[crassDataItem.id.toString()] : false,
      };
    });
  },
  defaultDisplayMode: 'list',
  maxItemCountInMixedContentTypeResultPage: 4,
  contentSpaceUrl: (urls: AppUrls, contentId: string) => urls.lensUrl(contentId),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter],
  getListViewColumns: () => [
    CommonColumns.name,
    CommonColumns.owner,
    CommonColumns.location,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'modified',
        dataKey: 'modifiedTime',
        sortable: false,
        headerText: Messages.column_updated(),
        direction: 'desc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DateTimeCell('modifiedTime'),
        ...{ growToFit: true },
      };
    },
    CommonColumns.description,
  ],
  getMetaDataRequests: GetMetaDataRequests.GetLensesRequest,
  getSortOptions: () => [SortBy.getRelevancySortSelection()],
  shouldBeAvailable: () => true,
  favoritable: () => true,
};
